import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Calculator, Zap, CheckCircle2 } from 'lucide-react';

interface CalculationStats {
  calculationsPerformed: number;
  totalTime: number;
  averageTime: number;
  lastCalculationType: string;
}

interface CalculationResult {
  result: number;
  formula: string;
  explanation: string;
  calculationType: string;
  processingTime: number;
}

const PercentageCalculatorTool: React.FC = () => {
  const [calculationType, setCalculationType] = useState<string>('basic');
  const [value1, setValue1] = useState<string>('');
  const [value2, setValue2] = useState<string>('');
  const [value3, setValue3] = useState<string>('');
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<CalculationStats>({
    calculationsPerformed: 0,
    totalTime: 0,
    averageTime: 0,
    lastCalculationType: ''
  });
  const [notification, setNotification] = useState<string>('');
  const [history, setHistory] = useState<CalculationResult[]>([]);

  const calculatePercentage = useCallback((): CalculationResult | null => {
    const startTime = performance.now();
    const num1 = parseFloat(value1);
    const num2 = parseFloat(value2);
    const num3 = parseFloat(value3);

    if (isNaN(num1) || (calculationType !== 'convert-decimal' && calculationType !== 'convert-fraction' && isNaN(num2))) {
      return null;
    }

    let resultValue: number;
    let formula: string;
    let explanation: string;

    switch (calculationType) {
      case 'basic':
        // What is X% of Y?
        resultValue = (num1 / 100) * num2;
        formula = `${num1}% of ${num2} = (${num1} ÷ 100) × ${num2} = ${resultValue}`;
        explanation = `To find ${num1}% of ${num2}, divide ${num1} by 100 and multiply by ${num2}.`;
        break;

      case 'is-what-percent':
        // X is what percentage of Y?
        resultValue = (num1 / num2) * 100;
        formula = `${num1} is ${resultValue}% of ${num2} = (${num1} ÷ ${num2}) × 100 = ${resultValue}%`;
        explanation = `To find what percentage ${num1} is of ${num2}, divide ${num1} by ${num2} and multiply by 100.`;
        break;

      case 'percent-of-what':
        // X is Y% of what number?
        resultValue = num1 / (num2 / 100);
        formula = `${num1} is ${num2}% of ${resultValue} = ${num1} ÷ (${num2} ÷ 100) = ${resultValue}`;
        explanation = `To find what number ${num1} is ${num2}% of, divide ${num1} by ${num2}% (converted to decimal).`;
        break;

      case 'percentage-change':
        // Percentage change from X to Y
        resultValue = ((num2 - num1) / num1) * 100;
        formula = `Change from ${num1} to ${num2} = ((${num2} - ${num1}) ÷ ${num1}) × 100 = ${resultValue}%`;
        explanation = `Percentage change is calculated as: ((New Value - Old Value) ÷ Old Value) × 100.`;
        break;

      case 'percentage-increase':
        // Increase X by Y%
        const increaseAmount = (num1 * num2) / 100;
        resultValue = num1 + increaseAmount;
        formula = `${num1} + ${num2}% = ${num1} + (${num1} × ${num2} ÷ 100) = ${num1} + ${increaseAmount} = ${resultValue}`;
        explanation = `To increase ${num1} by ${num2}%, calculate ${num2}% of ${num1} and add it to the original value.`;
        break;

      case 'percentage-decrease':
        // Decrease X by Y%
        const decreaseAmount = (num1 * num2) / 100;
        resultValue = num1 - decreaseAmount;
        formula = `${num1} - ${num2}% = ${num1} - (${num1} × ${num2} ÷ 100) = ${num1} - ${decreaseAmount} = ${resultValue}`;
        explanation = `To decrease ${num1} by ${num2}%, calculate ${num2}% of ${num1} and subtract it from the original value.`;
        break;

      case 'compound-percentage':
        // Compound percentage: X increased by Y% for Z periods
        if (isNaN(num3)) return null;
        resultValue = num1 * Math.pow(1 + (num2 / 100), num3);
        formula = `${num1} × (1 + ${num2}%)^${num3} = ${num1} × (1.${String(num2).padStart(2, '0')})^${num3} = ${resultValue}`;
        explanation = `Compound calculation: Original value × (1 + rate)^periods. This shows growth over ${num3} periods at ${num2}% per period.`;
        break;

      case 'tip-calculator':
        // Tip calculation: Bill amount X, tip percentage Y
        const tipAmount = (num1 * num2) / 100;
        resultValue = num1 + tipAmount;
        formula = `Bill: $${num1}, Tip: ${num2}% = $${tipAmount}, Total: $${resultValue}`;
        explanation = `Tip calculation: ${num2}% of $${num1} = $${tipAmount}. Total amount including tip: $${resultValue}.`;
        break;

      case 'discount-calculator':
        // Discount calculation: Original price X, discount Y%
        const discountAmount = (num1 * num2) / 100;
        resultValue = num1 - discountAmount;
        formula = `Original: $${num1}, Discount: ${num2}% = $${discountAmount}, Final: $${resultValue}`;
        explanation = `Discount calculation: ${num2}% off $${num1} = $${discountAmount} discount. Final price: $${resultValue}.`;
        break;

      case 'markup-calculator':
        // Markup calculation: Cost X, markup Y%
        const markupAmount = (num1 * num2) / 100;
        resultValue = num1 + markupAmount;
        formula = `Cost: $${num1}, Markup: ${num2}% = $${markupAmount}, Selling Price: $${resultValue}`;
        explanation = `Markup calculation: ${num2}% markup on $${num1} = $${markupAmount}. Selling price: $${resultValue}.`;
        break;

      case 'margin-calculator':
        // Profit margin: Revenue X, Cost Y
        const profit = num1 - num2;
        resultValue = (profit / num1) * 100;
        formula = `Margin = ((${num1} - ${num2}) ÷ ${num1}) × 100 = (${profit} ÷ ${num1}) × 100 = ${resultValue}%`;
        explanation = `Profit margin is calculated as: ((Revenue - Cost) ÷ Revenue) × 100. Profit: $${profit}.`;
        break;

      case 'tax-calculator':
        // Tax calculation: Amount X, tax rate Y%
        const taxAmount = (num1 * num2) / 100;
        resultValue = num1 + taxAmount;
        formula = `Amount: $${num1}, Tax: ${num2}% = $${taxAmount}, Total: $${resultValue}`;
        explanation = `Tax calculation: ${num2}% tax on $${num1} = $${taxAmount}. Total with tax: $${resultValue}.`;
        break;

      case 'convert-decimal':
        // Convert decimal to percentage
        resultValue = num1 * 100;
        formula = `${num1} as percentage = ${num1} × 100 = ${resultValue}%`;
        explanation = `To convert a decimal to percentage, multiply by 100. ${num1} = ${resultValue}%.`;
        break;

      case 'convert-fraction':
        // Convert fraction to percentage (X/Y)
        if (isNaN(num2) || num2 === 0) return null;
        resultValue = (num1 / num2) * 100;
        formula = `${num1}/${num2} as percentage = (${num1} ÷ ${num2}) × 100 = ${resultValue}%`;
        explanation = `To convert a fraction to percentage, divide numerator by denominator and multiply by 100.`;
        break;

      case 'grade-calculator':
        // Grade calculation: Points earned X, total points Y
        resultValue = (num1 / num2) * 100;
        formula = `Grade = (${num1} ÷ ${num2}) × 100 = ${resultValue}%`;
        explanation = `Grade percentage: (Points Earned ÷ Total Points) × 100 = ${resultValue}%.`;
        break;

      default:
        return null;
    }

    const endTime = performance.now();
    const processingTime = endTime - startTime;

    return {
      result: Math.round(resultValue * 100) / 100, // Round to 2 decimal places
      formula,
      explanation,
      calculationType,
      processingTime
    };
  }, [calculationType, value1, value2, value3]);

  const performCalculation = useCallback((): void => {
    const calculationResult = calculatePercentage();
    if (calculationResult) {
      setResult(calculationResult);
      setHistory(prev => [calculationResult, ...prev.slice(0, 9)]); // Keep last 10 calculations
      setStats(prev => ({
        calculationsPerformed: prev.calculationsPerformed + 1,
        totalTime: prev.totalTime + calculationResult.processingTime,
        averageTime: (prev.totalTime + calculationResult.processingTime) / (prev.calculationsPerformed + 1),
        lastCalculationType: calculationResult.calculationType
      }));
    } else {
      setResult(null);
    }
  }, [calculatePercentage]);

  useEffect(() => {
    if (value1 && value2) {
      performCalculation();
    }
  }, [value1, value2, value3, calculationType, performCalculation]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    if (!result) return;
    
    const textToCopy = `${result.formula}\n${result.explanation}\nResult: ${result.result}`;
    try {
      await navigator.clipboard.writeText(textToCopy);
      showNotification('Calculation copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadReport = (): void => {
    if (!result) return;
    
    const reportContent = `Percentage Calculation Report
Generated: ${new Date().toLocaleString()}

Calculation Type: ${result.calculationType}
Formula: ${result.formula}
Result: ${result.result}
Explanation: ${result.explanation}

Recent Calculations:
${history.slice(0, 5).map((calc, index) => 
  `${index + 1}. ${calc.formula} = ${calc.result}`
).join('\n')}

Statistics:
- Total Calculations: ${stats.calculationsPerformed}
- Average Processing Time: ${stats.averageTime.toFixed(2)}ms
`;

    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'percentage-calculation-report.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('Report downloaded!');
  };

  const clearAll = (): void => {
    setValue1('');
    setValue2('');
    setValue3('');
    setResult(null);
    setHistory([]);
    setStats({
      calculationsPerformed: 0,
      totalTime: 0,
      averageTime: 0,
      lastCalculationType: ''
    });
    showNotification('Cleared all data!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleCalculation = (): void => {
    setCalculationType('basic');
    setValue1('25');
    setValue2('80');
    setValue3('');
    showNotification('Sample calculation loaded!');
  };

  const getInputLabels = () => {
    switch (calculationType) {
      case 'basic':
        return { label1: 'Percentage (%)', label2: 'Of Value', label3: '', placeholder1: 'Enter percentage', placeholder2: 'Enter value' };
      case 'is-what-percent':
        return { label1: 'Value', label2: 'Total Value', label3: '', placeholder1: 'Enter value', placeholder2: 'Enter total' };
      case 'percent-of-what':
        return { label1: 'Value', label2: 'Percentage (%)', label3: '', placeholder1: 'Enter value', placeholder2: 'Enter percentage' };
      case 'percentage-change':
        return { label1: 'Original Value', label2: 'New Value', label3: '', placeholder1: 'Enter original', placeholder2: 'Enter new value' };
      case 'percentage-increase':
        return { label1: 'Original Value', label2: 'Increase (%)', label3: '', placeholder1: 'Enter value', placeholder2: 'Enter increase %' };
      case 'percentage-decrease':
        return { label1: 'Original Value', label2: 'Decrease (%)', label3: '', placeholder1: 'Enter value', placeholder2: 'Enter decrease %' };
      case 'compound-percentage':
        return { label1: 'Initial Value', label2: 'Rate (%)', label3: 'Periods', placeholder1: 'Enter initial value', placeholder2: 'Enter rate %', placeholder3: 'Enter periods' };
      case 'tip-calculator':
        return { label1: 'Bill Amount ($)', label2: 'Tip (%)', label3: '', placeholder1: 'Enter bill amount', placeholder2: 'Enter tip %' };
      case 'discount-calculator':
        return { label1: 'Original Price ($)', label2: 'Discount (%)', label3: '', placeholder1: 'Enter price', placeholder2: 'Enter discount %' };
      case 'markup-calculator':
        return { label1: 'Cost ($)', label2: 'Markup (%)', label3: '', placeholder1: 'Enter cost', placeholder2: 'Enter markup %' };
      case 'margin-calculator':
        return { label1: 'Revenue ($)', label2: 'Cost ($)', label3: '', placeholder1: 'Enter revenue', placeholder2: 'Enter cost' };
      case 'tax-calculator':
        return { label1: 'Amount ($)', label2: 'Tax Rate (%)', label3: '', placeholder1: 'Enter amount', placeholder2: 'Enter tax rate' };
      case 'convert-decimal':
        return { label1: 'Decimal Value', label2: '', label3: '', placeholder1: 'Enter decimal (e.g., 0.25)' };
      case 'convert-fraction':
        return { label1: 'Numerator', label2: 'Denominator', label3: '', placeholder1: 'Enter numerator', placeholder2: 'Enter denominator' };
      case 'grade-calculator':
        return { label1: 'Points Earned', label2: 'Total Points', label3: '', placeholder1: 'Enter points earned', placeholder2: 'Enter total points' };
      default:
        return { label1: 'Value 1', label2: 'Value 2', label3: '', placeholder1: 'Enter value 1', placeholder2: 'Enter value 2' };
    }
  };

  const inputLabels = getInputLabels();

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Percentage Calculator</h1>
              <p className="text-gray-300 text-sm">Advanced percentage calculator with multiple calculation types</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Calculation Type Selector */}
        <div className="mb-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Calculation Type</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { value: 'basic', label: 'Basic %' },
              { value: 'is-what-percent', label: 'Is What %' },
              { value: 'percent-of-what', label: '% of What' },
              { value: 'percentage-change', label: '% Change' },
              { value: 'percentage-increase', label: '% Increase' },
              { value: 'percentage-decrease', label: '% Decrease' },
              { value: 'compound-percentage', label: 'Compound %' },
              { value: 'tip-calculator', label: 'Tip Calculator' },
              { value: 'discount-calculator', label: 'Discount' },
              { value: 'markup-calculator', label: 'Markup' },
              { value: 'margin-calculator', label: 'Margin' },
              { value: 'tax-calculator', label: 'Tax' },
              { value: 'convert-decimal', label: 'Decimal→%' },
              { value: 'convert-fraction', label: 'Fraction→%' },
              { value: 'grade-calculator', label: 'Grade %' }
            ].map((type) => (
              <button
                key={type.value}
                onClick={() => setCalculationType(type.value)}
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  calculationType === type.value
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Calculation Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.calculationsPerformed}</div>
                <div className="text-gray-300 text-sm">Calculations</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.averageTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Avg Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{history.length}</div>
                <div className="text-gray-300 text-sm">History Items</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.lastCalculationType.replace('-', ' ')}</div>
                <div className="text-gray-300 text-sm">Last Type</div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input Values</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadSampleCalculation}
                  className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Zap className="w-4 h-4" />
                </button>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-4 flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {inputLabels.label1}
                </label>
                <input
                  type="number"
                  value={value1}
                  onChange={(e) => setValue1(e.target.value)}
                  placeholder={inputLabels.placeholder1}
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none text-lg"
                />
              </div>
              
              {inputLabels.label2 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {inputLabels.label2}
                  </label>
                  <input
                    type="number"
                    value={value2}
                    onChange={(e) => setValue2(e.target.value)}
                    placeholder={inputLabels.placeholder2}
                    className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none text-lg"
                  />
                </div>
              )}
              
              {inputLabels.label3 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {inputLabels.label3}
                  </label>
                  <input
                    type="number"
                    value={value3}
                    onChange={(e) => setValue3(e.target.value)}
                    placeholder={inputLabels.placeholder3}
                    className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none text-lg"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Result</h2>
              <div className="flex space-x-2">
                <button
                  onClick={copyToClipboard}
                  disabled={!result}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadReport}
                  disabled={!result}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 bg-gray-900 rounded-lg p-4 overflow-y-auto min-h-[400px]">
              {result ? (
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-purple-400 mb-2">
                      {result.result}
                      {calculationType.includes('percent') || calculationType === 'basic' || calculationType === 'is-what-percent' || calculationType === 'percentage-change' || calculationType === 'convert-decimal' || calculationType === 'convert-fraction' || calculationType === 'grade-calculator' || calculationType === 'margin-calculator' ? '%' : ''}
                    </div>
                    <div className="text-gray-400 text-sm">
                      Calculated in {result.processingTime.toFixed(2)}ms
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-600 pt-4">
                    <h4 className="text-white font-medium mb-2">Formula:</h4>
                    <div className="bg-gray-700 p-3 rounded font-mono text-sm text-green-400">
                      {result.formula}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-white font-medium mb-2">Explanation:</h4>
                    <div className="text-gray-300 text-sm leading-relaxed">
                      {result.explanation}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <Calculator className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Enter values to see calculation results</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History Section */}
        {history.length > 0 && (
          <div className="mt-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4">Recent Calculations</h3>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {history.slice(0, 5).map((calc, index) => (
                <div key={index} className="bg-gray-700 p-2 rounded text-sm">
                  <div className="text-white font-mono">{calc.formula}</div>
                  <div className="text-gray-400 text-xs">{calc.calculationType.replace('-', ' ')}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PercentageCalculatorTool;